import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  CAMPAIGN_NAME_TEMPLATE_HINT,
  DEFAULT_CAMPAIGN_NAME_TEMPLATE,
  type ExportScope,
} from './GoogleAdsExportModal';

// Budget CSV export modal (docs/GoogleCsvExport_Spec.md §2.3). Scope
// picker + Campaign name template (same default + hint as the Ads
// modal — both files in a single export pass cross-reference cleanly
// when the AM uses the same template). No Ad Group input.

type Props = {
  filteredCount: number;
  gateCount: number;
  busy?: boolean;
  // Pre-export validation (mirrors GoogleAdsExportModal). Optional —
  // when both counts are 0 / undefined, the existing flow runs.
  filteredViolationCount?: number;
  filteredLocationCount?: number;
  gateViolationCount?: number;
  gateLocationCount?: number;
  onCancel: () => void;
  onGenerate: (args: { scope: ExportScope; campaignNameTemplate: string }) => void;
  onShowErrors?: (scope: ExportScope) => void;
};

export function GoogleBudgetExportModal({
  filteredCount,
  gateCount,
  busy,
  filteredViolationCount = 0,
  filteredLocationCount = 0,
  gateViolationCount = 0,
  gateLocationCount = 0,
  onCancel,
  onGenerate,
  onShowErrors,
}: Props) {
  const [scope, setScope] = useState<ExportScope>('filtered');
  const [campaignNameTemplate, setCampaignNameTemplate] = useState<string>(
    DEFAULT_CAMPAIGN_NAME_TEMPLATE,
  );
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const selectedCount = scope === 'filtered' ? filteredCount : gateCount;
  const trimmedTemplate = campaignNameTemplate.trim();
  const violationCount =
    scope === 'filtered' ? filteredViolationCount : gateViolationCount;
  const locationCount =
    scope === 'filtered' ? filteredLocationCount : gateLocationCount;
  const hasViolations = violationCount > 0;
  const disabled = busy || selectedCount === 0 || trimmedTemplate.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="google-budget-export-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-5">
          <h2
            id="google-budget-export-title"
            className="text-lg font-semibold text-slate-900"
          >
            Export Budgets CSV
          </h2>

          {hasViolations && (
            <div
              role="alert"
              className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              ⚠ {violationCount} validation {violationCount === 1 ? 'error' : 'errors'} found
              {' '}across {locationCount} {locationCount === 1 ? 'location' : 'locations'}.
            </div>
          )}

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">Scope</legend>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="budget-export-scope"
                value="filtered"
                checked={scope === 'filtered'}
                onChange={() => setScope('filtered')}
                disabled={busy}
                className="mt-0.5"
              />
              <span>Currently filtered rows ({filteredCount} {filteredCount === 1 ? 'row' : 'rows'})</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="radio"
                name="budget-export-scope"
                value="all"
                checked={scope === 'all'}
                onChange={() => setScope('all')}
                disabled={busy}
                className="mt-0.5"
              />
              <span>All gate-passing rows ({gateCount} {gateCount === 1 ? 'row' : 'rows'})</span>
            </label>
          </fieldset>

          <div className="space-y-1">
            <label
              htmlFor="budget-export-campaign-template"
              className="block text-sm font-medium text-slate-700"
            >
              Campaign name template
            </label>
            <input
              id="budget-export-campaign-template"
              type="text"
              value={campaignNameTemplate}
              onChange={(e) => setCampaignNameTemplate(e.target.value)}
              disabled={busy}
              maxLength={500}
              className="w-full rounded border border-slate-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
            <p className="text-xs text-slate-500">{CAMPAIGN_NAME_TEMPLATE_HINT}</p>
          </div>

          {selectedCount === 0 && (
            <p className="text-sm text-amber-700">No rows match the selected scope.</p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            {hasViolations ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    onGenerate({ scope, campaignNameTemplate: trimmedTemplate })
                  }
                  disabled={disabled}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                >
                  {busy ? 'Generating…' : 'Export Anyway'}
                </button>
                <Button
                  onClick={() => onShowErrors?.(scope)}
                  disabled={busy || onShowErrors === undefined}
                >
                  View Errors
                </Button>
              </>
            ) : (
              <Button
                onClick={() =>
                  onGenerate({ scope, campaignNameTemplate: trimmedTemplate })
                }
                disabled={disabled}
              >
                {busy ? 'Generating…' : 'Generate CSV'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
